import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { SkillTreeEffectsOptions } from './skill-tree-effects';
import {
    SKILL_TREE_BURST_DURATION,
    SKILL_TREE_INTRO_SPEED_MOD,
    buildSkillTreeIntroSchedule,
    findSkillTreeBursts,
    getStoredSkillTreeSnapshot,
    toSkillTreeSnapshot,
    storeSkillTreeSnapshot,
    type SkillTreeBurst,
    type SkillTreeIntroDirection,
    type SkillTreeIntroSchedule,
} from './skill-tree-intro';
import { SkillTreeIntroClock } from './skill-tree-intro-clock';
import type { SkillTreeModel } from './skill-tree-model';

interface UseSkillTreeIntroOptions {
    readonly characterId: string;
    readonly model: SkillTreeModel;
    /** The tree should be showing; the intro plays each time this turns on and reverses when it turns off. */
    readonly isVisible: boolean;
    /** Motion is allowed: "Character Home effects" is Minimal or All and the OS doesn't ask for reduced motion. */
    readonly isMotionAllowed: boolean;
}

export interface SkillTreeIntroPlayback {
    /** The tree is on screen: while visible, and while it shrinks back into the sigil after being hidden. */
    readonly isShown: boolean;
    /** The schedule while the intro is running or held shrunk back, or undefined once the tree is settled (or without motion). */
    readonly schedule: SkillTreeIntroSchedule | undefined;
    /** The intro's clock, for the scene's parts (see `SkillTreeIntroClockContext`); null while settled. */
    readonly clock: SkillTreeIntroClock | null;
    /** How the effects layer times its light and bursts right now. */
    readonly effects: SkillTreeEffectsOptions;
    /** Skips the intro to the finished tree (a click on the tree); the bursts play at once. */
    readonly skip: () => void;
}

const NO_EFFECT_TIMING: SkillTreeEffectsOptions = {};

/**
 * Runs the Skills tab intro (docs/gameplay/skill-tree-ui.md, Intro
 * Animation) on one clock (`SkillTreeIntroClock`). Each time the tree is
 * shown the clock runs forward, growing it out of the sigil, and bursts
 * then mark every step gained since this viewer last looked; each time it
 * is hidden the clock runs back, shrinking it into the sigil before it
 * goes. Switching back and forth partway just turns the clock around, so
 * the tree carries on from exactly where it is. A click on the tree or any
 * key skips to the finished tree, with the bursts at once. Without motion
 * the layer's plain fade is all.
 */
export function useSkillTreeIntro({ characterId, model, isVisible, isMotionAllowed }: UseSkillTreeIntroOptions): SkillTreeIntroPlayback {
    const schedule = useMemo(() => buildSkillTreeIntroSchedule(model), [model]);
    /** The parts are on the clock: from each open until the tree has fully grown, and from each close until the next open. */
    const [running, setRunning] = useState(false);
    /** Still on screen while shrinking back after being hidden. */
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(closing);
    closingRef.current = closing;
    const [effects, setEffects] = useState<SkillTreeEffectsOptions>(NO_EFFECT_TIMING);
    const burstsRef = useRef<readonly SkillTreeBurst[]>([]);
    const latestRef = useRef({ characterId, model, isMotionAllowed });
    latestRef.current = { characterId, model, isMotionAllowed };

    const handleSettle = useCallback((direction: SkillTreeIntroDirection): void => {
        if (direction === 'forward') {
            // Fully grown: the parts leave the clock, since they look the same without it.
            setRunning(false);
            return;
        }
        // Fully shrunk back: off screen, holding that pose until the next open.
        setClosing(false);
    }, []);
    const [clock] = useState(() => new SkillTreeIntroClock(schedule.timeline, handleSettle));
    useEffect(() => clock.setTimeline(schedule.timeline), [clock, schedule]);
    useEffect(() => () => clock.dispose(), [clock]);

    const wasVisibleRef = useRef(false);
    // A layout effect, so the clock turns around before the next frame is painted, never a frame late.
    useLayoutEffect(() => {
        const opened = isVisible && !wasVisibleRef.current;
        const closed = !isVisible && wasVisibleRef.current;
        wasVisibleRef.current = isVisible;
        if (!opened && !closed) return;
        const latest = latestRef.current;
        if (opened) {
            const previous = getStoredSkillTreeSnapshot(latest.characterId);
            storeSkillTreeSnapshot(latest.characterId, toSkillTreeSnapshot(latest.model));
            burstsRef.current = findSkillTreeBursts(previous, latest.model);
        }
        if (!latest.isMotionAllowed) {
            // No motion: settled straight away, and the layer's plain fade shows or hides it.
            clock.finish();
            setClosing(false);
            setEffects(NO_EFFECT_TIMING);
            return;
        }
        if (opened) {
            // Turning around mid-reverse carries on from where the tree is; otherwise grow from just the sigil.
            if (!closingRef.current) clock.seek(0);
            // The effects layer joins wherever the clock already is.
            const offset = clock.position;
            setEffects({ schedule, offset, bursts: burstsRef.current, burstDelay: (schedule.timeline - offset) / SKILL_TREE_INTRO_SPEED_MOD });
            setClosing(false);
            setRunning(true);
            clock.play('forward');
        } else {
            setEffects(NO_EFFECT_TIMING);
            setClosing(true);
            setRunning(true);
            clock.play('reverse');
        }
    }, [isVisible, clock, schedule]);

    // While open, what the tree shows is what the player has seen: a gain that lands now, or another
    // character switched to, is recorded, so it doesn't burst again at the next open (#330).
    useEffect(() => {
        if (isVisible) {
            storeSkillTreeSnapshot(characterId, toSkillTreeSnapshot(model));
        }
    }, [isVisible, characterId, model]);

    // The bursts finish a moment after the tree has grown; then the effects layer drops its intro timing.
    useEffect(() => {
        if (running || closing || effects === NO_EFFECT_TIMING) return;
        const timer = window.setTimeout(() => setEffects(NO_EFFECT_TIMING), SKILL_TREE_BURST_DURATION * 1000);
        return () => window.clearTimeout(timer);
    }, [running, closing, effects]);

    // A click on the tree (see `SkillTree`) or any key skips the intro to the finished tree.
    // Clicks elsewhere, such as on another tab, don't: leaving turns the clock around instead.
    const skip = useCallback((): void => {
        if (!isVisible || !running) return;
        clock.finish();
        setEffects({ bursts: burstsRef.current, burstDelay: 0 });
    }, [clock, running, isVisible]);
    useEffect(() => {
        if (!running || !isVisible) return;
        window.addEventListener('keydown', skip, true);
        return () => window.removeEventListener('keydown', skip, true);
    }, [running, isVisible, skip]);

    return {
        isShown: isVisible || closing,
        schedule: running ? schedule : undefined,
        clock: running ? clock : null,
        effects,
        skip,
    };
}
